import { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Copy, MessageCircle, Calendar, Clock, ClipboardList, Truck, Store, MapPin, Phone, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Header } from "../components/Header";
import { CartSheet } from "../components/CartSheet";
import { clientApi, formatCOP } from "../lib/api";
import { toast } from "sonner";

// Format an ISO date (YYYY-MM-DD) into a Colombian Spanish full label.
const formatDeliveryDate = (iso) => {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, m - 1, d, 12, 0, 0);
    return date.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return iso;
  }
};

export default function OrderSuccess() {
  const { number } = useParams();
  const { state } = useLocation();
  const order = state?.order;
  const [deliveryInfo, setDeliveryInfo] = useState(null);

  useEffect(() => {
    clientApi.get("/portal/delivery-info").then(({ data }) => setDeliveryInfo(data)).catch(() => {});
  }, []);

  const copyNumber = () => {
    navigator.clipboard.writeText(number);
    toast.success("Número de pedido copiado");
  };

  return (
    <div className="min-h-screen bg-cm-cream" data-testid="order-success-page">
      <Header />
      <CartSheet />

      <main className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
        <Card className="bg-white border-cm-border shadow-none p-8 sm:p-12 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">¡Listo!</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">Tu pedido fue recibido</h1>
          <p className="text-cm-ink-soft mt-3 max-w-md mx-auto">
            Nuestro equipo comercial procesará tu pedido y se pondrá en contacto contigo para confirmar la entrega.
          </p>

          <div className="mt-8 inline-flex items-center gap-3 bg-cm-cream-alt border border-cm-border rounded-md px-5 py-3">
            <span className="text-xs uppercase tracking-widest text-cm-ink-soft">N° de seguimiento</span>
            <span className="font-mono font-bold text-lg text-cm-red" data-testid="order-number">{number}</span>
            <button onClick={copyNumber} className="text-cm-ink-soft hover:text-cm-ink" data-testid="copy-order-number" title="Copiar">
              <Copy className="h-4 w-4" />
            </button>
          </div>

          {order && (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 text-left bg-cm-cream-alt/50 rounded-md p-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Productos</p>
                <p className="font-mono font-semibold mt-0.5">{order.lineas?.length || 0}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Total</p>
                <p className="font-mono font-bold mt-0.5 text-cm-red">{formatCOP(order.total)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Estado</p>
                <p className="font-medium mt-0.5 capitalize">Recibido</p>
              </div>
            </div>
          )}
        </Card>

        <Card className="mt-6 bg-cm-yellow/15 border-cm-yellow shadow-none p-6">
          <h2 className="font-serif text-lg mb-3 flex items-center gap-2">
            {order?.tipo_entrega === "recoger" ? (
              <><Store className="h-4 w-4 text-cm-red" /> Recoges tu pedido en bodega</>
            ) : (
              <><Truck className="h-4 w-4 text-cm-red" /> Te llevamos tu pedido</>
            )}
          </h2>
          <div className="space-y-2 text-sm text-cm-ink">
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-cm-ink-soft mt-0.5 flex-shrink-0" />
              {deliveryInfo?.estimate?.max ? (
                <p>
                  {order?.tipo_entrega === "recoger" ? "Listo entre " : "Llega entre "}
                  <strong>{deliveryInfo.estimate.min.label_dia} {deliveryInfo.estimate.min.label_corto}</strong>
                  {" y "}
                  <strong>{deliveryInfo.estimate.max.label_dia} {deliveryInfo.estimate.max.label_corto}</strong>.
                </p>
              ) : order?.fecha_entrega ? (
                <p>
                  {order.tipo_entrega === "recoger" ? "Disponible para recoger el " : "Entrega programada para el "}
                  <strong>{formatDeliveryDate(order.fecha_entrega)}</strong>.
                </p>
              ) : (
                <p>Recibirás llamada o WhatsApp para confirmar día y hora exacta.</p>
              )}
            </div>
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-cm-ink-soft mt-0.5 flex-shrink-0" />
              <p>
                {order?.tipo_entrega === "recoger"
                  ? "Te avisaremos por WhatsApp cuando tu pedido esté listo en bodega."
                  : "Las entregas no se realizan los domingos ni festivos colombianos."}
              </p>
            </div>
          </div>

          {order?.tipo_entrega === "recoger" && deliveryInfo?.bodega && (
            <div className="mt-4 pt-4 border-t border-cm-yellow/60" data-testid="success-bodega-info">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium mb-2">
                Punto de recogida
              </p>
              <p className="font-medium text-cm-ink mb-1">{deliveryInfo.bodega.nombre}</p>
              {deliveryInfo.bodega.direccion && (
                <p className="text-sm flex items-start gap-2 text-cm-ink-soft">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-cm-red" />
                  <span>{deliveryInfo.bodega.direccion}</span>
                </p>
              )}
              {deliveryInfo.bodega.horario && (
                <p className="text-sm flex items-start gap-2 text-cm-ink-soft mt-1.5">
                  <Clock className="h-4 w-4 mt-0.5 flex-shrink-0 text-cm-red" />
                  <span>{deliveryInfo.bodega.horario}</span>
                </p>
              )}
              {deliveryInfo.bodega.telefono && (
                <p className="text-sm flex items-start gap-2 text-cm-ink-soft mt-1.5">
                  <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 text-cm-red" />
                  <a href={`tel:${deliveryInfo.bodega.telefono.replace(/\s/g, "")}`} className="hover:text-cm-red hover:underline">
                    {deliveryInfo.bodega.telefono}
                  </a>
                </p>
              )}
              {deliveryInfo.bodega.maps_url && (
                <a
                  href={deliveryInfo.bodega.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-3 py-2 rounded-md bg-cm-ink text-cm-cream hover:bg-cm-ink/90 transition-colors text-sm font-medium"
                  data-testid="success-bodega-maps-link"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  Cómo llegar
                  <ExternalLink className="h-3 w-3 opacity-70" />
                </a>
              )}
            </div>
          )}
        </Card>

        <Card className="mt-6 bg-white border-cm-border shadow-none p-6">
          <h2 className="font-serif text-lg mb-3 flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-cm-red" /> ¿Consultar el estado de tu pedido?
          </h2>
          <p className="text-sm text-cm-ink-soft mb-4">
            Escríbenos por WhatsApp con tu número de seguimiento <span className="font-mono text-cm-ink font-semibold">{number}</span>:
          </p>
          <a
            href={`https://wa.me/573106203436?text=${encodeURIComponent(
              `Hola, soy ${order?.razon_social || order?.almacen || "cliente"} (NIT ${order?.nit || ""}), quiero consultar el estado del pedido ${number}.`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors text-sm font-medium"
            data-testid="support-whatsapp-link"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Atención al cliente · 310 6203 436
          </a>
          <p className="text-xs text-cm-ink-soft mt-3">
            ✉️ También puedes escribir a <a className="text-cm-red hover:underline" href="mailto:gerenciacomercialcmcys@gmail.com">gerenciacomercialcmcys@gmail.com</a>
          </p>
        </Card>

        <div className="mt-8 text-center flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
          <Button asChild variant="outline" className="border-cm-ink h-11" data-testid="my-orders-from-success-btn">
            <Link to="/mis-pedidos"><ClipboardList className="mr-2 h-4 w-4" /> Ver mis pedidos</Link>
          </Button>
          <Button asChild className="btn-cm-primary h-11" data-testid="new-order-btn">
            <Link to="/catalogo">Hacer otro pedido <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
